import { Rate, Select } from "antd";
import React, { useEffect, useRef, useState } from "react";
import {
  getRatingsAvailable,
  ratingEventOptions
} from "../../../../api/events";

const DEFAULT_GENERAL_SKILLS = [
  "Punctuality",
  "Appearance",
  "Protocols",
  "Engagement",
  "Leadership",
];

const getDefaultGeneralSkills = () =>
  DEFAULT_GENERAL_SKILLS.map((label) => ({
    uuid: label,
    label,
    value: null
  }));

const getLabel = (item) => {
  if (typeof item === "string") return item;

  return (
    item?.name ||
    item?.label ||
    item?.display_name ||
    item?.dropdown_menu_name ||
    item?.rating_name ||
    item?.skill_name ||
    item?.event_role?.name ||
    item?.role?.name ||
    ""
  );
};

const normalizeRatingItems = (source, fallbackLabels = []) => {
  if (Array.isArray(source)) {
    return source
      .map((item, index) => {
        const label = getLabel(item) || fallbackLabels[index] || "";
        const uuid = item?.uuid || item?.id || label;

        return label && uuid
          ? {
            uuid,
            label,
            value: item?.value ?? null
          }
          : null;
      })
      .filter(Boolean);
  }

  if (source && typeof source === "object") {
    return Object.entries(source).map(([uuid, value], index) => ({
      uuid,
      label: fallbackLabels[index] || uuid,
      value
    }));
  }

  return [];
};

const getRatingValueMap = (items) =>
  items.reduce((acc, item) => {
    acc[item.uuid] = item.value ?? null;
    return acc;
  }, {});

const normalizeRatingsAvailable = (data) => {
  const generalSource =
    data?.general_skills ||
    data?.generalSkills ||
    data?.general_ratings ||
    data?.generalRatings ||
    data?.ratings ||
    data?.skills ||
    [];

  const eventRoleSource =
    data?.event_roles ||
    data?.eventRoles ||
    data?.roles ||
    [];

  const skillSource =
    data?.skills ||
    data?.role_ratings ||
    data?.roleRatings ||
    data?.roles_involved ||
    [];

  const generalSkills = normalizeRatingItems(
    generalSource,
    DEFAULT_GENERAL_SKILLS
  );

  const eventRoles = normalizeRatingItems(eventRoleSource);
  const skills = normalizeRatingItems(skillSource);

  return {
    generalSkills: generalSkills.length
      ? generalSkills
      : getDefaultGeneralSkills(),
    eventRoles,
    skills
  };
};

const PerformanceRatings = ({
  personId,
  resetKey,
  initialEventId,
  readOnly = false,
  onEventSelect,
  onGeneralChange,
  onRatingsAvailable
}) => {
  const [eventId, setEventId] = useState("");
  const [eventOptions, setEventOptions] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [generalSkills, setGeneralSkills] = useState(
    getDefaultGeneralSkills()
  );
  const [ratings, setRatings] = useState({});
  const [hoveredValues, setHoveredValues] = useState({});

  useEffect(() => {
    if (!resetKey) return;

    setEventId("");
    setRatings({});
    setGeneralSkills(getDefaultGeneralSkills());
    setHoveredValues({});
    setRatingsLoading(false);
  }, [resetKey]);

  const handleRatingChange = (skill, value) => {
    const updatedRatings = {
      ...ratings,
      [skill.uuid]: value
    };

    const updatedGeneralSkills = generalSkills.map((item) =>
      item.uuid === skill.uuid
        ? {
          ...item,
          value
        }
        : item
    );

    setRatings(updatedRatings);
    setGeneralSkills(updatedGeneralSkills);

    onGeneralChange?.(
      updatedRatings,
      updatedGeneralSkills
    );
  };

  const fetchCompletedEvents = async () => {
    if (!personId || eventsLoading) return;

    setEventsLoading(true);

    try {
      const response = await ratingEventOptions({
        number: 1,
        size: 100,
        people: [personId],
        // Events already reviewed stay selectable so that feedback can be edited
        excludeCurrentUserEvents: false
      });

      const events = response?.data || [];

      setEventOptions(events);
    } catch (error) {
      console.error('Error while fetchin completed events', error);
      setEventOptions([]);
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    if (!personId) {
      setEventOptions([]);
      return;
    }

    fetchCompletedEvents();
  }, [personId]);

  const resetRatings = () => {
    const defaultGeneralSkills = getDefaultGeneralSkills();

    setRatings({});
    setGeneralSkills(defaultGeneralSkills);
    setHoveredValues({});

    onGeneralChange?.({}, defaultGeneralSkills);

    onRatingsAvailable?.({
      generalSkills: defaultGeneralSkills,
      eventRoles: [],
      skills: []
    });
  };

  const fetchRatingsAvailable = async (selectedEventId) => {
    if (!selectedEventId || !personId) {
      resetRatings();
      return;
    }

    setRatingsLoading(true);

    try {
      const response = await getRatingsAvailable({
        eventUUID: selectedEventId,
        personUUID: personId
      });

      // v2: { stats_obj: { ratings } }, plus the note's uuid/desc when the
      // current user has already reviewed this person for this event
      const data = response?.data || {};
      const normalized = normalizeRatingsAvailable(
        data.stats_obj?.ratings || {}
      );

      const generalRatingValues = getRatingValueMap(
        normalized.generalSkills
      );

      setGeneralSkills(normalized.generalSkills);
      setRatings(generalRatingValues);
      setHoveredValues({});

      onGeneralChange?.(
        generalRatingValues,
        normalized.generalSkills
      );

      onRatingsAvailable?.({
        ...normalized,
        existingFeedback: data.uuid
          ? { uuid: data.uuid, desc: data.desc || "" }
          : null
      });
    } catch (error) {
      resetRatings();
    } finally {
      setRatingsLoading(false);
    }
  };

  const handleEventChange = async (value) => {
    if (!value) {
      setEventId("");
      onEventSelect?.("");
      resetRatings();
      return;
    }

    setEventId(value);
    onEventSelect?.(value);

    await fetchRatingsAvailable(value);
  };

  // Deep link (?event_uuid=...): prefill the event once the dropdown options load
  const hasPreselected = useRef(false);

  useEffect(() => {
    if (!initialEventId || hasPreselected.current) return;
    if (!eventOptions.some((event) => event.uuid === initialEventId)) return;

    hasPreselected.current = true;
    handleEventChange(initialEventId);
  }, [initialEventId, eventOptions]);

  const eventDropdownOptions = eventOptions.map((event) => ({
    value: event.uuid,
    label:
      event.name ||
      event.display_name ||
      event.dropdown_menu_name
  }));

  return (
    <div>
      <div className="border border-grayBorder p-3 rounded-md mt-3 relative">
        <label className="block text-sm font-medium mb-1">
          Event <span className="text-red-500">*</span>
        </label>

        <Select
          showSearch
          allowClear
          value={eventId || undefined}
          onChange={handleEventChange}
          className="w-full"
          disabled={!personId}
          onFocus={fetchCompletedEvents}
          onDropdownVisibleChange={(open) => open && fetchCompletedEvents()}
          loading={eventsLoading}
          options={eventDropdownOptions}
          optionFilterProp="label"
          placeholder={
            eventsLoading
              ? "Loading events..."
              : "Select completed event"
          }
          filterOption={(inputValue, option) =>
            (option?.label || "")
              .toLowerCase()
              .includes(inputValue.toLowerCase())
          }
          getPopupContainer={(trigger) => trigger.parentNode}
        />
      </div>

      <div className="flex flex-col mt-6">
        <h3 className="font-semibold mb-4">
          {ratingsLoading
            ? "Loading ratings..."
            : "General Skills "}
          <span className="text-red-500">*</span>
        </h3>

        <div className="relative">
          <div className="absolute top-0 bottom-0 left-1/2 w-px bg-grayBorder" />

          <div className="grid grid-cols-2 gap-y-4 gap-x-12">
            {generalSkills.map((skill) => {
              const displayValue =
                hoveredValues[skill.uuid] !== undefined &&
                  hoveredValues[skill.uuid] !== null
                  ? hoveredValues[skill.uuid]
                  : ratings[skill.uuid];

              return (
                <div
                  key={skill.uuid}
                  className="flex items-center justify-between pb-2"
                >
                  <span className="text-sm">
                    {skill.label}
                  </span>

                  <div className="flex items-center">
                    {displayValue > 0 && (
                      <span className="text-sm mr-2">
                        {displayValue}
                      </span>
                    )}

                    <Rate
                      allowHalf
                      value={ratings[skill.uuid] || 0}
                      disabled={readOnly || !eventId || ratingsLoading}
                      onChange={(value) =>
                        handleRatingChange(skill, value)
                      }
                      onHoverChange={(value) =>
                        setHoveredValues((prev) => ({
                          ...prev,
                          [skill.uuid]: value
                        }))
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceRatings;
